<!-- Icon-only menu trigger. Sibling to TriggerButton for the no-label case. -->

<template>
  <Tooltip.Hint v-if="tooltip" :text="label">
    <button
      type="button"
      :aria-label="label"
      :data-emphasis-identifier="emphasisId"
      :class="buttonClass"
    >
      <component :is="iconMappingMicro[icon]" :class="iconClass" />
    </button>
  </Tooltip.Hint>
  <button
    v-else
    type="button"
    :aria-label="label"
    :data-emphasis-identifier="emphasisId"
    :class="buttonClass"
  >
    <component :is="iconMappingMicro[icon]" :class="iconClass" />
  </button>
</template>

<script setup lang="ts">
import { computed } from "vue";

import Tooltip from "@/design_system/Tooltip";
import { iconMappingMicro } from "@/icons/icon-mapping-micro";

// Note: no `id` prop — the menu trigger forwards its own generated `id` onto
// this button (for `aria-labelledby` wiring), and a prop named `id` would
// swallow it. The deep-link hook uses a separately-named `emphasisId`.
const props = withDefaults(
  defineProps<{
    label?: string;
    icon?: keyof typeof iconMappingMicro;
    variant?: "default" | "compact";
    tooltip?: boolean;
    emphasisId?: string;
  }>(),
  {
    label: `Actions`,
    icon: `ellipsis-horizontal`,
    variant: `default`,
  }
);

// `aria-expanded` (set by the menu trigger), not `data-[state=open]`, drives the
// open state: when wrapped in a tooltip, the tooltip also writes `data-state`.
const buttonClass = computed(() =>
  props.variant === `compact`
    ? `grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground outline-2 outline-action transition hover:bg-card focus-visible:outline active:bg-card aria-expanded:bg-card`
    : `shrink-0 rounded-full border border-transparent p-[0.4375rem] text-gray-700 outline-2 outline-action transition hover:bg-gray-200 focus-visible:outline aria-expanded:bg-gray-200 sm:px-1.5 sm:py-1 dark:text-muted-foreground dark:hover:bg-card dark:aria-expanded:bg-card`
);

const iconClass = computed(() =>
  props.variant === `compact` ? `size-4` : `size-4 sm:h-5`
);
</script>
